import React, { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "../ui/components/Button";
import {
  generateReport,
  getBalanceSheetSummary,
  getEffectiveDateInfo,
} from "../features/cfo/data/api/trialBalanceApi";
import {
  InlineExcelViewer,
  clearParsedReportCache,
} from "../features/cfo/presentation/components/InlineExcelViewer";
import { BalanceSheetSummarySection } from "../features/cfo/presentation/components/BalanceSheetSummarySection";
import {
  setStoredReportMonths,
  getStoredReportInfo,
  setStoredReportInfo,
  clearStoredReportInfo,
  shouldClearReportCacheOnLoad,
} from "../features/cfo/utils/reportingMonth";

export default function BalanceSheetPage() {
  const [startMonth, setStartMonth] = useState<string>("");
  const [endMonth, setEndMonth] = useState<string>("");
  const [isLoadingDates, setIsLoadingDates] = useState<boolean>(true);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [reportId, setReportId] = useState<number | null>(null);
  const [reportName, setReportName] = useState<string>("");
  const [asOfLabel, setAsOfLabel] = useState<string>("");
  const [summaryCards, setSummaryCards] = useState<Array<{
    key: string;
    label: string;
    value: number;
    value_formatted: string;
    previous_value: number;
    trend: "up" | "down" | "neutral";
    trend_text: string;
    change_percent: number | null;
  }> | null>(null);
  const [isLoadingSummary, setIsLoadingSummary] = useState<boolean>(false);
  const [summaryError, setSummaryError] = useState<string>("");

  // Auto-apply guards (same pattern as ProfitAndLossPage)
  const autoApplyFiredRef = useRef(false);
  const handleApplyRef = useRef<(() => Promise<void>) | null>(null);

  const fetchSummary = useCallback(async (endMonthVal: string) => {
    if (!endMonthVal) return;
    setIsLoadingSummary(true);
    setSummaryError("");
    try {
      const res = await getBalanceSheetSummary({ end_month: endMonthVal });
      setSummaryCards(res.data.cards ?? null);
      setAsOfLabel(res.data.as_of?.label ?? "");
    } catch (err: any) {
      setSummaryError(err?.message ?? "Failed to load summary.");
      setSummaryCards(null);
    } finally {
      setIsLoadingSummary(false);
    }
  }, []);

  // Initialise months from backend reporting period (FY start → latest closed month).
  useEffect(() => {
    const initializeMonths = async (): Promise<void> => {
      try {
        if (shouldClearReportCacheOnLoad()) {
          clearStoredReportInfo();
          clearParsedReportCache();
        }

        const effectiveDateInfo = await getEffectiveDateInfo();
        const { fy_start_month, latest_closed_month, latest_closed_month_label } =
          effectiveDateInfo.data;
        if (!fy_start_month || !latest_closed_month) {
          throw new Error("No closed month available to generate report.");
        }

        setStartMonth(fy_start_month);
        setEndMonth(latest_closed_month);
        setStoredReportMonths(fy_start_month, latest_closed_month);
        setAsOfLabel(latest_closed_month_label || "");

        const storedReport = getStoredReportInfo();
        if (
          storedReport &&
          storedReport.startMonth === fy_start_month &&
          storedReport.endMonth === latest_closed_month
        ) {
          setReportId(storedReport.reportId);
          setReportName(storedReport.reportName);
        }
      } catch (err: unknown) {
        console.error("Failed to initialize report months:", err);
        setError((err as any)?.message || "Failed to load reporting period.");
      } finally {
        setIsLoadingDates(false);
      }
    };

    initializeMonths();
  }, []);

  // Refetch summary when endMonth changes (e.g. user changes month picker)
  useEffect(() => {
    if (!isLoadingDates && endMonth) {
      fetchSummary(endMonth);
    }
  }, [endMonth, isLoadingDates, fetchSummary]);

  const getFirstDayOfMonth = (monthStr: string): string => {
    return `${monthStr}-01`;
  };

  const getLastDayOfMonth = (monthStr: string): string => {
    const [year, month] = monthStr.split("-").map(Number);
    const lastDay = new Date(year, month, 0).getDate();
    return `${monthStr}-${String(lastDay).padStart(2, "0")}`;
  };

  const handleApply = async (): Promise<void> => {
    setError("");

    if (!startMonth || !endMonth) {
      setError("Please select both start and end months.");
      return;
    }

    if (startMonth > endMonth) {
      setError("Start month must be before or equal to end month.");
      return;
    }

    setStoredReportMonths(startMonth, endMonth);
    setIsGenerating(true);
    try {
      const response = await generateReport({
        from_date: getFirstDayOfMonth(startMonth),
        to_date: getLastDayOfMonth(endMonth),
        group_by: "monthwise",
        save_report: true,
      });

      const newReportId = response.data.report_id;
      if (!newReportId) {
        throw new Error("Report ID not returned from server.");
      }

      const newReportName = response.data.report_name || "";
      setReportId(newReportId);
      setReportName(newReportName);
      setStoredReportInfo({
        startMonth,
        endMonth,
        reportId: newReportId,
        reportName: newReportName,
      });
      await fetchSummary(endMonth);
    } catch (err: any) {
      setError(err.message || "Failed to generate report. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  // Keep latest handleApply in a ref so auto-apply can call it safely.
  useEffect(() => {
    handleApplyRef.current = handleApply;
  });

  // Auto-trigger Apply once per mount after dates initialised.
  useEffect(() => {
    if (autoApplyFiredRef.current) return;
    if (isLoadingDates) return;
    if (!startMonth || !endMonth) return;
    if (reportId) return;
    autoApplyFiredRef.current = true;
    const timeoutId = window.setTimeout(() => {
      handleApplyRef.current?.();
    }, 1500);
    return () => window.clearTimeout(timeoutId);
  }, [isLoadingDates, startMonth, endMonth, reportId]);

  const headerActions = (
    <>
      <div className="flex items-center gap-2">
        <label className="text-xs font-caption text-subtext-color">
          Start Month
        </label>
        <input
          type="month"
          value={startMonth}
          onChange={(e) => setStartMonth(e.target.value)}
          disabled={isLoadingDates}
          className="px-2 py-1.5 border border-solid border-neutral-border rounded-md text-sm font-body disabled:opacity-50 disabled:cursor-not-allowed"
        />
      </div>
      <div className="flex items-center gap-2">
        <label className="text-xs font-caption text-subtext-color">
          End Month
        </label>
        <input
          type="month"
          value={endMonth}
          onChange={(e) => setEndMonth(e.target.value)}
          disabled={isLoadingDates}
          className="px-2 py-1.5 border border-solid border-neutral-border rounded-md text-sm font-body disabled:opacity-50 disabled:cursor-not-allowed"
        />
      </div>
      <Button
        variant="brand-primary"
        onClick={handleApply}
        disabled={isGenerating || isLoadingDates || !startMonth || !endMonth}
        size="medium"
        className="h-[35px]"
      >
        {isGenerating ? "Applying..." : "Apply"}
      </Button>
    </>
  );

  const emptyState = isLoadingDates
    ? "Loading..."
    : "Select start and end months and click Apply to view the report.";

  return (
    <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-6 bg-neutral-50 px-6 py-6 overflow-auto">
      {error && (
        <div className="w-full px-4 py-2 bg-red-50 border border-red-200 rounded-md text-body font-body text-red-700">
          {error}
        </div>
      )}

      <BalanceSheetSummarySection
        cards={summaryCards}
        isLoading={isLoadingSummary}
        error={summaryError}
        asOfLabel={asOfLabel || undefined}
      />

      <div
        className={`flex w-full grow shrink-0 basis-0 overflow-auto rounded-md border border-neutral-border bg-default-background ${reportId ? "min-h-[80vh]" : ""}`}
      >
        <InlineExcelViewer
          reportId={reportId}
          reportName={reportName}
          targetSheet="BS"
          headerActions={headerActions}
          showHeaderWhenEmpty
          fallbackTitle="Balance Sheet"
          headerAsOfLabel={asOfLabel || undefined}
          isGenerating={isGenerating}
          emptyState={emptyState}
          showStatusBar={false}
        />
      </div>
    </div>
  );
}
